import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, from } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { AuditService } from '../../../../services/audit.service';
import { AuthService } from '../../../../services/auth.service';
import { AccountService } from '../../../../services/account.service';
import { ZonesSlotService } from '../../../../services/zones-slot.service';
import {
  UserAuditLog,
  AuditActionType,
  AuditStatus,
  AuditSeverity,
  AuditCategory,
  AuditLogFilter,
  AuditLogSummary,
  ZonesSlotActivity,
  ZonesSlotActivityFilter,
  ZonesSlotActivitySummary,
  PaginatedAuditLogs,
  PaginatedZonesSlotActivities
} from '../../../../models/audit.model';
import { User } from '../../../../models/user.model';
import { Account } from '../../../../models/account.model';

@Component({
  selector: 'app-audit-logs',
  templateUrl: './audit-logs.component.html',
  styleUrls: ['./audit-logs.component.css']
})
export class AuditLogsComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  // Data
  auditLogs: UserAuditLog[] = [];
  filteredLogs: UserAuditLog[] = [];
  summary: AuditLogSummary = {};
  currentUser: User;

  // Account cache for performance
  accountCache: { [key: string]: Account } = {};

  // Zone and slot cache for performance
  zoneCache: { [key: string]: string } = {}; // zone_id -> zone_name
  slotCache: { [key: string]: string } = {}; // slot_id -> slot_name

  // Pagination (cursor-based)
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  auditLogsHasMore = false;
  auditLogsCursors: (string | null)[] = [null]; // Store cursor for each page (index 0 = page 1)

  // Loading states
  loading = false;
  exporting = false;

  // Filter form
  filter: AuditLogFilter = {
    start_date: this.getDefaultStartDate(),
    end_date: new Date(),
    limit: 10 // Now we fetch only what we need per page
  };

  // Dropdown options
  actionTypes = Object.values(AuditActionType);
  categories = Object.values(AuditCategory);
  severities = Object.values(AuditSeverity);
  statuses = Object.values(AuditStatus);

  // UI state
  showFilters = false;
  selectedLogs: string[] = [];
  sortField = 'timestamp';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Zone Slot Activity data
  zoneSlotActivities: ZonesSlotActivity[] = [];
  filteredSlotActivities: ZonesSlotActivity[] = [];
  slotActivitySummary: ZonesSlotActivitySummary = {};

  // Zone Slot Activity pagination (cursor-based)
  slotCurrentPage = 1;
  slotItemsPerPage = 10;
  slotTotalItems = 0;
  slotActivitiesHasMore = false;
  slotActivitiesCursors: (string | null)[] = [null]; // Store cursor for each page

  // Zone Slot Activity filter
  slotFilter: ZonesSlotActivityFilter = {
    // start_date: this.getDefaultStartDate(),
    // end_date: new Date(),
    limit: 10 // Now we fetch only what we need per page
  };

  // Zone Slot Activity UI state
  loadingSlotActivity = false;
  selectedSlotActivities: string[] = [];
  slotSortField = 'timestamp';
  slotSortDirection: 'asc' | 'desc' = 'desc';

  constructor(
    private auditService: AuditService,
    private authService: AuthService,
    private accountService: AccountService,
    private zonesSlotService: ZonesSlotService
  ) { }

  ngOnInit(): void {
    // Get current user
    this.authService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        if (user) {
          this.loadAuditLogs();
          this.loadSummary();
          this.loadZoneSlotActivities();
          this.loadSlotActivitySummary();
        }
      });

    // Log page access
    this.auditService.logNavigation('/admin/audit-logs', 'Audit Logs');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadAuditLogs(): Promise<void> {
    this.loading = true;
    console.log('🔍 Loading audit logs with filter:', this.filter);

    try {
      // Get the cursor for the current page
      const cursor = this.auditLogsCursors[this.currentPage - 1] || null;

      // Use paginated query with cursor
      const paginatedFilter: AuditLogFilter = {
        ...this.filter,
        limit: this.itemsPerPage,
        lastDocId: cursor
      };

      console.log('🔍 Fetching page', this.currentPage, 'with cursor:', cursor);

      const result: PaginatedAuditLogs = await this.auditService.getAuditLogsPaginated(paginatedFilter);

      console.log('📋 Paginated query returned:', result.logs.length, 'logs, hasMore:', result.hasMore);

      this.auditLogs = result.logs;
      this.filteredLogs = result.logs;
      this.auditLogsHasMore = result.hasMore;

      // Store the cursor for the next page
      if (result.lastDocId && result.hasMore) {
        this.auditLogsCursors[this.currentPage] = result.lastDocId;
      }

      // Update total items (approximate for cursor-based pagination)
      this.totalItems = this.filteredLogs.length + (result.hasMore ? this.itemsPerPage : 0);

      // Preload account names for visible logs
      this.preloadAccountNames();

    } catch (error) {
      console.error('❌ Error loading audit logs:', error);
      this.auditLogs = [];
      this.filteredLogs = [];
    } finally {
      this.loading = false;
    }
  }

  async loadSummary(): Promise<void> {
    try {
      this.summary = await this.auditService.getAuditSummary(this.filter);
    } catch (error) {
      console.error('Error loading audit summary:', error);
    }
  }

  applyFilter(): void {
    this.currentPage = 1;
    this.auditLogsCursors = [null]; // Reset cursors when filter changes
    this.loadAuditLogs();
    this.loadSummary();
  }

  clearFilter(): void {
    this.filter = {
      start_date: this.getDefaultStartDate(),
      end_date: new Date(),
      limit: this.itemsPerPage
    };
    this.auditLogsCursors = [null]; // Reset cursors
    this.applyFilter();
  }

  sort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'desc';
    }
    // Note: For true server-side sorting, we would need to reload from Firebase
    // For now, sort is handled by the server (timestamp desc by default)
    console.log('Sort requested:', field, this.sortDirection);
  }

  getPaginatedLogs(): UserAuditLog[] {
    // Data is already paginated from the server, return as is
    return this.filteredLogs;
  }

  goToPage(page: number): void {
    // For cursor-based pagination, we can only go to next page if we have the cursor
    // or go back to previous pages we've visited
    if (page < 1) return;

    // Can only go forward if we have more data and cursor exists
    if (page > this.currentPage && !this.auditLogsHasMore) return;

    // Can only go to pages we have cursors for (or page 1)
    if (page > this.currentPage + 1) return;

    this.currentPage = page;
    this.loadAuditLogs();
  }

  goToNextPage(): void {
    if (this.auditLogsHasMore) {
      this.currentPage++;
      this.loadAuditLogs();
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadAuditLogs();
    }
  }

  getTotalPages(): number {
    // For cursor-based pagination, we don't know the exact total
    // Return current page + 1 if there's more data
    return this.auditLogsHasMore ? this.currentPage + 1 : this.currentPage;
  }

  getPageNumbers(): number[] {
    // For cursor-based pagination, just show current page and next if available
    const pages = [this.currentPage];
    if (this.auditLogsHasMore) {
      pages.push(this.currentPage + 1);
    }
    return pages;
  }

  toggleLogSelection(logId: string): void {
    const index = this.selectedLogs.indexOf(logId);
    if (index > -1) {
      this.selectedLogs.splice(index, 1);
    } else {
      this.selectedLogs.push(logId);
    }
  }

  selectAllLogs(): void {
    if (this.selectedLogs.length === this.getPaginatedLogs().length) {
      this.selectedLogs = [];
    } else {
      this.selectedLogs = this.getPaginatedLogs().map(log => log.id);
    }
  }

  async exportLogs(format: 'csv' | 'json' | 'pdf' = 'csv'): Promise<void> {
    this.exporting = true;

    try {
      const logsToExport = this.selectedLogs.length > 0
        ? this.filteredLogs.filter(log => this.selectedLogs.includes(log.id))
        : this.filteredLogs;

      if (format === 'csv') {
        this.exportToCSV(logsToExport);
      } else if (format === 'json') {
        this.exportToJSON(logsToExport);
      } else if (format === 'pdf') {
        this.exportToPDF(logsToExport);
      }

      // Log the export action
      await this.auditService.logExport(
        `audit_logs_${format}`,
        `audit_logs_${new Date().toISOString().split('T')[0]}.${format}`,
        logsToExport.length
      );

    } catch (error) {
      console.error('Error exporting logs:', error);
    } finally {
      this.exporting = false;
    }
  }

  private exportToCSV(logs: UserAuditLog[]): void {
    const headers = [
      'Timestamp',
      'Account',
      'User Email',
      'User Name',
      'User Role',
      'Action Type',
      'Action Description',
      'Resource Type',
      'Resource Name',
      'Status',
      'Severity',
      'Category',
      'IP Address',
      'Changes Summary'
    ];

    const csvContent = [
      headers.join(','),
      ...logs.map(log => {
        // Helper function to properly format timestamp
        const formatTimestamp = (timestamp: any): string => {
          if (!timestamp) return '';
          try {
            if (timestamp.seconds) {
              return new Date(timestamp.seconds * 1000).toLocaleString();
            } else if (timestamp.toDate) {
              return timestamp.toDate().toLocaleString();
            } else {
              return new Date(timestamp).toLocaleString();
            }
          } catch (error) {
            return '';
          }
        };

        // Helper function to escape CSV fields
        const escapeCsvField = (field: any): string => {
          if (field === null || field === undefined) return '';
          const str = String(field);
          // Escape double quotes and wrap in quotes if contains comma, newline, or quote
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        };

        return [
          formatTimestamp(log.timestamp),
          this.accountCache[log.account_key]?.account_name || log.account_key || 'No Account',
          log.user_email || '',
          log.user_name || '',
          log.user_role || '',
          log.action_type || '',
          log.action_description || '',
          log.resource_type || '',
          log.resource_name || '',
          log.status || '',
          log.severity || '',
          log.category || '',
          log.ip_address || '',
          log.changes_summary || ''
        ].map(field => escapeCsvField(field)).join(',');
      })
    ].join('\n');

    this.downloadFile(csvContent, `audit_logs_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
  }

  private exportToJSON(logs: UserAuditLog[]): void {
    const jsonContent = JSON.stringify(logs, null, 2);
    this.downloadFile(jsonContent, `audit_logs_${new Date().toISOString().split('T')[0]}.json`, 'application/json');
  }

  private exportToPDF(logs: UserAuditLog[]): void {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Helper function to format timestamp
    const formatTimestamp = (timestamp: any): string => {
      if (!timestamp) return '';
      try {
        if (timestamp.seconds) {
          return new Date(timestamp.seconds * 1000).toLocaleString();
        } else if (timestamp.toDate) {
          return timestamp.toDate().toLocaleString();
        } else {
          return new Date(timestamp).toLocaleString();
        }
      } catch (error) {
        return '';
      }
    };

    // Add title
    doc.setFontSize(16);
    doc.text('Audit Logs Report', 14, 15);

    // Add metadata
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    doc.text(`Total Records: ${logs.length}`, 14, 27);

    // Prepare table data
    const tableData = logs.map(log => [
      formatTimestamp(log.timestamp),
      this.accountCache[log.account_key]?.account_name || log.account_key || 'No Account',
      log.user_email || '',
      log.action_type || '',
      log.action_description || '',
      log.resource_type || '',
      log.status || '',
      log.severity || ''
    ]);

    // Add table
    autoTable(doc, {
      head: [['Timestamp', 'Account', 'User', 'Action', 'Description', 'Resource', 'Status', 'Severity']],
      body: tableData,
      startY: 32,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [235, 47, 70], // #eb2f46
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 35 }, // Timestamp
        1: { cellWidth: 30 }, // Account
        2: { cellWidth: 40 }, // User
        3: { cellWidth: 25 }, // Action
        4: { cellWidth: 50 }, // Description
        5: { cellWidth: 30 }, // Resource
        6: { cellWidth: 20 }, // Status
        7: { cellWidth: 20 }  // Severity
      },
      margin: { top: 32 }
    });

    // Save the PDF
    doc.save(`audit_logs_${new Date().toISOString().split('T')[0]}.pdf`);
  }

  private downloadFile(content: string, filename: string, contentType: string): void {
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  getActionTypeColor(actionType: AuditActionType): string {
    switch (actionType) {
      case AuditActionType.DELETE:
      case AuditActionType.BULK_DELETE:
        return 'text-red-600';
      case AuditActionType.UPDATE:
      case AuditActionType.BULK_UPDATE:
        return 'text-yellow-600';
      case AuditActionType.CREATE:
        return 'text-green-600';
      case AuditActionType.LOGIN_FAILED:
        return 'text-red-500';
      case AuditActionType.LOGIN:
      case AuditActionType.LOGOUT:
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  }

  getSeverityColor(severity: AuditSeverity): string {
    switch (severity) {
      case AuditSeverity.CRITICAL:
        return 'bg-red-600 text-white';
      case AuditSeverity.HIGH:
        return 'bg-red-400 text-white';
      case AuditSeverity.MEDIUM:
        return 'bg-yellow-400 text-gray-800';
      case AuditSeverity.LOW:
        return 'bg-green-400 text-white';
      default:
        return 'bg-gray-400 text-white';
    }
  }

  getStatusColor(status: AuditStatus): string {
    switch (status) {
      case AuditStatus.SUCCESS:
        return 'bg-green-100 text-green-800';
      case AuditStatus.FAILED:
        return 'bg-red-100 text-red-800';
      case AuditStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case AuditStatus.CANCELLED:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  formatTimestamp(timestamp: any): string {
    if (!timestamp) return '';

    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);

    return date.toLocaleString();
  }

  private getDefaultStartDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default to last 30 days
    return date;
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  /**
   * Get account name from account_key
   */
  getAccountName(accountKey: string): string {
    if (!accountKey) {
      return 'No Account';
    }

    // Check cache first
    if (this.accountCache[accountKey]) {
      return this.accountCache[accountKey].account_name || accountKey;
    }

    // If not in cache, fetch it and return placeholder for now
    this.loadAccountName(accountKey);
    return 'Loading...';
  }

  /**
   * Load account name and cache it
   */
  private loadAccountName(accountKey: string): void {
    if (this.accountCache[accountKey]) {
      return; // Already loading or loaded
    }

    // Set loading placeholder to prevent multiple requests
    this.accountCache[accountKey] = { account_name: 'Loading...' } as Account;

    from(this.accountService.getAccountByKey(accountKey))
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (account) => {
          if (account) {
            this.accountCache[accountKey] = account;
          } else {
            this.accountCache[accountKey] = { account_name: 'Unknown Account' } as Account;
          }
        },
        (error) => {
          console.error('Error loading account:', accountKey, error);
          this.accountCache[accountKey] = { account_name: 'Error Loading' } as Account;
        }
      );
  }

  /**
   * Preload all account names for visible logs
   */
  private preloadAccountNames(): void {
    const uniqueAccountKeys = [...new Set(this.filteredLogs.map(log => log.account_key).filter(key => key))];

    uniqueAccountKeys.forEach(accountKey => {
      if (!this.accountCache[accountKey]) {
        this.loadAccountName(accountKey);
      }
    });
  }

  /**
   * Load zone name and cache it
   */
  private loadZoneName(zone_id: string): void {
    if (this.zoneCache[zone_id]) {
      return; // Already loading or loaded
    }

    // Mark as loading
    this.zoneCache[zone_id] = 'Loading...';

    console.log("-> :", this.zoneCache[zone_id]);
    // Search through all accounts to find the zone
    from(this.accountService.getAccounts()).pipe(
      takeUntil(this.destroy$),
      map(accounts => {
        console.log(accounts);
        for (const account of accounts) {
          if (account.settings?.zones) {
            const zone = account.settings.zones.find(z => z.zone_id === zone_id);
            if (zone) {
              return zone.zone_name || zone_id;
            }
          }
        }
        return 'Unknown Zone';
      })
    ).subscribe(
      zoneName => {
        this.zoneCache[zone_id] = zoneName;
      },
      error => {
        console.error('Error loading zone:', zone_id, error);
        this.zoneCache[zone_id] = 'Error Loading';
      }
    );
  }

  /**
   * Load slot name and cache it
   */
  private loadSlotName(zone_id: string, slot_id: string): void {
    const cacheKey = `${zone_id}_${slot_id}`;

    if (this.slotCache[cacheKey]) {
      return; // Already loading or loaded
    }

    // Mark as loading
    this.slotCache[cacheKey] = 'Loading...';

    // Use the ZonesSlotService to get slot information
    from(this.zonesSlotService.getSlotsByZoneIdAndSlotId(zone_id, slot_id)).pipe(
      takeUntil(this.destroy$),
      map(slot => {
        if (slot) {
          // Return the slot name from the matching slot
          return slot.slot_name || slot.slot_code || slot_id;
        }
        return 'Unknown Slot';
      })
    ).subscribe(
      slotName => {
        this.slotCache[cacheKey] = slotName;
      },
      error => {
        console.error('Error loading slot:', zone_id, slot_id, error);
        this.slotCache[cacheKey] = 'Error Loading';
      }
    );
  }

  /**
   * Preload zone and slot names for visible activities
   */
  private preloadZoneAndSlotNames(): void {
    const uniqueZoneIds = [...new Set(this.filteredSlotActivities.map(activity => activity.zone_id).filter(id => id))];

    // For slots, we need to work with zone-slot pairs
    const uniqueZoneSlotPairs = [...new Set(
      this.filteredSlotActivities
        .filter(activity => activity.zone_id && activity.slot_id)
        .map(activity => `${activity.zone_id}_${activity.slot_id}`)
    )];

    uniqueZoneIds.forEach(zoneId => {
      if (!this.zoneCache[zoneId]) {
        this.loadZoneName(zoneId);
      }
    });

    uniqueZoneSlotPairs.forEach(pairKey => {
      if (!this.slotCache[pairKey]) {
        const [zoneId, slotId] = pairKey.split('_');
        this.loadSlotName(zoneId, slotId);
      }
    });
  }

  /**
   * Get the ending index for pagination display
   */
  getPaginationEndIndex(): number {
    return ((this.currentPage - 1) * this.itemsPerPage) + this.filteredLogs.length;
  }

  /**
   * Get starting index for pagination display
   */
  getPaginationStartIndex(): number {
    return this.filteredLogs.length > 0 ? ((this.currentPage - 1) * this.itemsPerPage) + 1 : 0;
  }

  // Zone Slot Activity Methods
  async loadZoneSlotActivities(): Promise<void> {
    this.loadingSlotActivity = true;
    console.log('🔍 Loading zone slot activities with filter:', this.slotFilter);

    try {
      // Get the cursor for the current page
      const cursor = this.slotActivitiesCursors[this.slotCurrentPage - 1] || null;

      // Use paginated query with cursor
      const paginatedFilter: ZonesSlotActivityFilter = {
        ...this.slotFilter,
        limit: this.slotItemsPerPage,
        lastDocId: cursor
      };

      console.log('🔍 Fetching slot activity page', this.slotCurrentPage, 'with cursor:', cursor);

      const result: PaginatedZonesSlotActivities = await this.zonesSlotService.getZoneSlotActivitiesPaginated(paginatedFilter);

      console.log('📋 Paginated query returned:', result.activities.length, 'activities, hasMore:', result.hasMore);

      this.zoneSlotActivities = result.activities;
      this.filteredSlotActivities = result.activities;
      this.slotActivitiesHasMore = result.hasMore;

      // Store the cursor for the next page
      if (result.lastDocId && result.hasMore) {
        this.slotActivitiesCursors[this.slotCurrentPage] = result.lastDocId;
      }

      // Update total items (approximate for cursor-based pagination)
      this.slotTotalItems = this.filteredSlotActivities.length + (result.hasMore ? this.slotItemsPerPage : 0);

      // Preload zone and slot names
      this.preloadZoneAndSlotNames();
    } catch (error) {
      console.error('❌ Error loading zone slot activities:', error);
      console.error('❌ Error details:', error.message);
      this.zoneSlotActivities = [];
      this.filteredSlotActivities = [];
    } finally {
      this.loadingSlotActivity = false;
    }
  }

  async loadSlotActivitySummary(): Promise<void> {
    try {
      if (this.slotFilter.account_key) {
        this.slotActivitySummary = await this.zonesSlotService.getZonesSlotActivitySummary(this.slotFilter);
      }
    } catch (error) {
      console.error('Error loading slot activity summary:', error);
    }
  }

  applySlotActivityFilter(): void {
    this.slotCurrentPage = 1;
    this.slotActivitiesCursors = [null]; // Reset cursors when filter changes
    this.loadZoneSlotActivities();
    this.loadSlotActivitySummary();
  }

  clearSlotActivityFilter(): void {
    this.slotFilter = {
      // start_date: this.getDefaultStartDate(),
      // end_date: new Date(),
      limit: this.slotItemsPerPage
    };
    this.slotActivitiesCursors = [null]; // Reset cursors
    this.applySlotActivityFilter();
  }

  sortSlotActivity(field: string): void {
    if (this.slotSortField === field) {
      this.slotSortDirection = this.slotSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.slotSortField = field;
      this.slotSortDirection = 'desc';
    }
    // Note: For true server-side sorting, we would need to reload from Firebase
    // For now, sort is handled by the server (timestamp desc by default)
    console.log('Slot activity sort requested:', field, this.slotSortDirection);
  }

  getPaginatedSlotActivities(): ZonesSlotActivity[] {
    // Data is already paginated from the server, return as is
    return this.filteredSlotActivities;
  }

  goToSlotActivityPage(page: number): void {
    // For cursor-based pagination, we can only go to next page if we have the cursor
    // or go back to previous pages we've visited
    if (page < 1) return;

    // Can only go forward if we have more data
    if (page > this.slotCurrentPage && !this.slotActivitiesHasMore) return;

    // Can only go to pages we have cursors for (or page 1)
    if (page > this.slotCurrentPage + 1) return;

    this.slotCurrentPage = page;
    this.loadZoneSlotActivities();
  }

  goToNextSlotActivityPage(): void {
    if (this.slotActivitiesHasMore) {
      this.slotCurrentPage++;
      this.loadZoneSlotActivities();
    }
  }

  goToPreviousSlotActivityPage(): void {
    if (this.slotCurrentPage > 1) {
      this.slotCurrentPage--;
      this.loadZoneSlotActivities();
    }
  }

  getSlotActivityTotalPages(): number {
    // For cursor-based pagination, we don't know the exact total
    return this.slotActivitiesHasMore ? this.slotCurrentPage + 1 : this.slotCurrentPage;
  }

  getSlotActivityPageNumbers(): number[] {
    // For cursor-based pagination, just show current page and next if available
    const pages = [this.slotCurrentPage];
    if (this.slotActivitiesHasMore) {
      pages.push(this.slotCurrentPage + 1);
    }
    return pages;
  }

  getSlotActivityPaginationEndIndex(): number {
    return ((this.slotCurrentPage - 1) * this.slotItemsPerPage) + this.filteredSlotActivities.length;
  }

  getSlotActivityPaginationStartIndex(): number {
    return this.filteredSlotActivities.length > 0 ? ((this.slotCurrentPage - 1) * this.slotItemsPerPage) + 1 : 0;
  }

  toggleSlotActivitySelection(activityId: string): void {
    const index = this.selectedSlotActivities.indexOf(activityId);
    if (index > -1) {
      this.selectedSlotActivities.splice(index, 1);
    } else {
      this.selectedSlotActivities.push(activityId);
    }
  }

  selectAllSlotActivities(): void {
    if (this.selectedSlotActivities.length === this.getPaginatedSlotActivities().length) {
      this.selectedSlotActivities = [];
    } else {
      this.selectedSlotActivities = this.getPaginatedSlotActivities().map(activity => activity.id);
    }
  }

  getSlotActionTypeColor(actionType: number): string {
    switch (actionType) {
      case 1:
        return 'bg-green-100 text-green-800'; // Entry/Create
      case 2:
        return 'bg-yellow-100 text-yellow-800'; // Update
      case 3:
        return 'bg-red-100 text-red-800'; // Exit/Delete
      case 4:
        return 'bg-blue-100 text-blue-800'; // Maintenance
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getSlotActionTypeLabel(actionType: number): string {
    switch (actionType) {
      case 1:
        return 'Vacant';
      case 2:
        return 'Add Plate';
      case 3:
        return 'Auto Vacant';
      case 4:
        return 'Auto Expired';
      default:
        return 'Unknown';
    }
  }

  getZoneName(zone_id: string): string {
    if (!zone_id) {
      return 'Unknown Zone';
    }

    // Check cache first
    if (this.zoneCache[zone_id]) {
      return this.zoneCache[zone_id];
    }

    // Load zone name asynchronously
    this.loadZoneName(zone_id);
    return 'Loading...';
  }

  getSlotName(zone_id: string, slot_id: string): string {
    if (!slot_id || !zone_id) {
      return 'Unknown Slot';
    }

    // Use a compound key for cache since slot_id might not be unique across zones
    const cacheKey = `${zone_id}_${slot_id}`;
    // Check cache first
    if (this.slotCache[cacheKey]) {
      return this.slotCache[cacheKey];
    }

    // Load slot name asynchronously
    this.loadSlotName(zone_id, slot_id);
    return 'Loading...';
  }

  formatSlotTimestamp(timestamp: any): string {
    if (!timestamp) return '';

    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);

    return date.toLocaleString();
  }

  async exportSlotActivities(format: 'csv' | 'json' | 'pdf' = 'csv'): Promise<void> {
    this.exporting = true;

    try {
      const activitiesToExport = this.selectedSlotActivities.length > 0
        ? this.filteredSlotActivities.filter(activity => this.selectedSlotActivities.includes(activity.id))
        : this.filteredSlotActivities;

      if (format === 'csv') {
        this.exportSlotActivitiesToCSV(activitiesToExport);
      } else if (format === 'json') {
        this.exportSlotActivitiesToJSON(activitiesToExport);
      } else if (format === 'pdf') {
        this.exportSlotActivitiesToPDF(activitiesToExport);
      }

      // Log the export action
      await this.auditService.logExport(
        `zone_slot_activities_${format}`,
        `zone_slot_activities_${new Date().toISOString().split('T')[0]}.${format}`,
        activitiesToExport.length
      );

    } catch (error) {
      console.error('Error exporting slot activities:', error);
    } finally {
      this.exporting = false;
    }
  }

  private exportSlotActivitiesToCSV(activities: ZonesSlotActivity[]): void {
    const headers = [
      'Timestamp',
      'Zone',
      'Slot',
      'Action Type',
      'Log ID',
      'Previous Log ID',
      'Updated By ID',
      'Updated By Name'
    ];

    // Helper function to format timestamp
    const formatTimestamp = (timestamp: any): string => {
      if (!timestamp) return '';
      try {
        if (timestamp.seconds) {
          return new Date(timestamp.seconds * 1000).toLocaleString();
        } else if (timestamp.toDate) {
          return timestamp.toDate().toLocaleString();
        } else {
          return new Date(timestamp).toLocaleString();
        }
      } catch (error) {
        return '';
      }
    };

    // Helper function to escape CSV fields
    const escapeCsvField = (field: any): string => {
      if (field === null || field === undefined) return '';
      const str = String(field);
      // Escape double quotes and wrap in quotes if contains comma, newline, or quote
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.join(','),
      ...activities.map(activity => [
        formatTimestamp(activity.timestamp),
        this.getZoneName(activity.zone_id),
        this.getSlotName(activity.zone_id, activity.slot_id),
        this.getSlotActionTypeLabel(activity.action_type),
        activity.log_id || '',
        activity.previous_log_id || '',
        activity.updated_by_id || '',
        activity.updated_by_name || ''
      ].map(field => escapeCsvField(field)).join(','))
    ].join('\n');

    this.downloadFile(csvContent, `zone_slot_activities_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
  }

  private exportSlotActivitiesToJSON(activities: ZonesSlotActivity[]): void {
    const jsonContent = JSON.stringify(activities, null, 2);
    this.downloadFile(jsonContent, `zone_slot_activities_${new Date().toISOString().split('T')[0]}.json`, 'application/json');
  }

  private exportSlotActivitiesToPDF(activities: ZonesSlotActivity[]): void {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Helper function to format timestamp
    const formatTimestamp = (timestamp: any): string => {
      if (!timestamp) return '';
      try {
        if (timestamp.seconds) {
          return new Date(timestamp.seconds * 1000).toLocaleString();
        } else if (timestamp.toDate) {
          return timestamp.toDate().toLocaleString();
        } else {
          return new Date(timestamp).toLocaleString();
        }
      } catch (error) {
        return '';
      }
    };

    // Add title
    doc.setFontSize(16);
    doc.text('Zone Slot Activity Report', 14, 15);

    // Add metadata
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    doc.text(`Total Records: ${activities.length}`, 14, 27);

    // Prepare table data
    const tableData = activities.map(activity => [
      formatTimestamp(activity.timestamp),
      this.getZoneName(activity.zone_id),
      this.getSlotName(activity.zone_id, activity.slot_id),
      this.getSlotActionTypeLabel(activity.action_type),
      activity.log_id || '',
      activity.updated_by_name || ''
    ]);

    // Add table
    autoTable(doc, {
      head: [['Timestamp', 'Zone', 'Slot', 'Action Type', 'Log ID', 'Updated By']],
      body: tableData,
      startY: 32,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [235, 47, 70], // #eb2f46
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 40 }, // Timestamp
        1: { cellWidth: 50 }, // Zone
        2: { cellWidth: 50 }, // Slot
        3: { cellWidth: 35 }, // Action Type
        4: { cellWidth: 50 }, // Log ID
        5: { cellWidth: 45 }  // Updated By
      },
      margin: { top: 32 }
    });

    // Save the PDF
    doc.save(`zone_slot_activities_${new Date().toISOString().split('T')[0]}.pdf`);
  }
}